import { useId, useState } from 'react';

import Button from 'components/ui/button';
import { PessoaSugerida } from 'services/realizacao.service';
import { FOCO_VISIVEL } from 'utils/foco';
import { nomeDoParticipante } from 'pages/RealizacaoDestaque/identidade-do-participante';

import { AvaliacaoDaInclusao } from './elegibilidade-do-participante';
import { CAMPO, ROTULO, RostoDaPessoa } from './componentes-do-cadastro';
import { combinaComFiltro } from './situacao-do-participante';
import { SecaoDeSugestoes } from './sugestoes-de-participantes';

export type EstadoDasSugestoes = { tipo: 'carregando' } | { tipo: 'erro' } | { tipo: 'pronto'; secoes: SecaoDeSugestoes[] };

interface PainelDeSugestoesProps {
  estado: EstadoDasSugestoes;
  /** Chaves de quem já está na lista de participantes (SUG-15, SUG-17). */
  naLista: Set<string>;
  /** As mesmas regras da busca, para mostrar quem não pode entrar e por quê (SUG-16). */
  avaliar: (pessoa: PessoaSugerida) => AvaliacaoDaInclusao;
  /** Recebe as marcadas na ordem do painel; as regras rodam de novo ao incluir (SUG-12). */
  onIncluir: (pessoas: PessoaSugerida[]) => void;
}

/** Quantas pessoas cada seção mostra antes de "Ver todas" (SUG-08). */
const VISIVEIS_DE_INICIO = 10;

/** A partir de quantas pessoas no painel aparece "Filtrar sugestões" (mesmo critério da lista de participantes). */
const MINIMO_PARA_FILTRO = 11;

const STATUS = 'm-0 text-xs text-content';

/**
 * Pessoas sugeridas na etapa de Participantes: quem já participou com o autor, a equipe e a equipe do gestor superior.
 * O painel só mostra e seleciona; quem usa decide quem está na lista, avalia e inclui.
 */
export default function PainelDeSugestoes({ estado, naLista, avaliar, onIncluir }: PainelDeSugestoesProps) {
  const tituloId = useId();
  const filtroId = useId();
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [termo, setTermo] = useState('');

  function alternar(chaves: string[], marcar: boolean) {
    setMarcadas(atuais => {
      const proximas = new Set(atuais);
      chaves.forEach(chave => (marcar ? proximas.add(chave) : proximas.delete(chave)));
      return proximas;
    });
  }

  const secoes = estado.tipo === 'pronto' ? estado.secoes : [];
  const total = secoes.reduce((soma, secao) => soma + secao.pessoas.length, 0);
  const filtrando = termo.trim().length > 0;
  const selecionaveis = (pessoas: PessoaSugerida[]) =>
    pessoas.filter(pessoa => !naLista.has(pessoa._id) && avaliar(pessoa).aceita);
  // Quem entrou na lista ou deixou de passar nas regras sai da seleção sem precisar desmarcar. Marcadas que o filtro
  // esconde continuam valendo.
  const aIncluir = secoes.flatMap(secao => selecionaveis(secao.pessoas)).filter(pessoa => marcadas.has(pessoa._id));

  function incluir() {
    onIncluir(aIncluir);
    setMarcadas(new Set());
  }

  return (
    <section aria-labelledby={tituloId} className="flex flex-col gap-3 rounded-jj-lg bg-surface-100 px-4 py-3.5">
      <h3 id={tituloId} className="m-0 font-display text-sm font-extrabold text-brand-blue-dark">
        Sugestões
      </h3>

      {estado.tipo === 'carregando' && (
        <p role="status" className={`${STATUS} animate-pulse`}>
          Carregando sugestões…
        </p>
      )}

      {estado.tipo === 'erro' && (
        <p role="status" className={STATUS}>
          Não foi possível carregar as sugestões. Você pode buscar pelo nome ou pela matrícula.
        </p>
      )}

      {total >= MINIMO_PARA_FILTRO && (
        <div>
          <label htmlFor={filtroId} className={ROTULO}>
            Filtrar sugestões
          </label>
          <input
            id={filtroId}
            type="search"
            value={termo}
            onChange={evento => setTermo(evento.target.value)}
            placeholder="Nome ou matrícula"
            className={`${CAMPO} ${FOCO_VISIVEL}`}
          />
        </div>
      )}

      {secoes.map(secao => {
        const pessoas = filtrando ? secao.pessoas.filter(pessoa => combinaComFiltro(pessoa, termo)) : secao.pessoas;

        return (
          <Secao
            key={secao.origem}
            secao={{ ...secao, pessoas }}
            filtrando={filtrando}
            naLista={naLista}
            avaliar={avaliar}
            marcadas={marcadas}
            selecionaveis={selecionaveis(pessoas).map(pessoa => pessoa._id)}
            onAlternar={alternar}
          />
        );
      })}

      {aIncluir.length > 0 && (
        <Button variant="secundario" size="sm" className="self-start" onClick={incluir}>
          Incluir selecionados ({aIncluir.length})
        </Button>
      )}
    </section>
  );
}

interface SecaoProps {
  secao: SecaoDeSugestoes;
  /** Com filtro, a seção mostra todas as que casam e diz quando nenhuma casa. */
  filtrando: boolean;
  naLista: Set<string>;
  avaliar: (pessoa: PessoaSugerida) => AvaliacaoDaInclusao;
  marcadas: Set<string>;
  selecionaveis: string[];
  onAlternar: (chaves: string[], marcar: boolean) => void;
}

function Secao({ secao, filtrando, naLista, avaliar, marcadas, selecionaveis, onAlternar }: SecaoProps) {
  const tituloId = useId();
  const [aberta, setAberta] = useState(false);
  const total = secao.pessoas.length;
  const todasVisiveis = aberta || filtrando;
  const visiveis = todasVisiveis ? secao.pessoas : secao.pessoas.slice(0, VISIVEIS_DE_INICIO);
  const todasMarcadas = selecionaveis.length > 0 && selecionaveis.every(chave => marcadas.has(chave));

  return (
    <section aria-labelledby={tituloId} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 id={tituloId} className="m-0 text-xs font-bold text-brand-blue-dark">
          {secao.titulo} ({total})
        </h4>
        {total > 0 && (
          <label className="flex items-center gap-1.5 text-jj-label font-bold text-brand-blue">
            <input
              type="checkbox"
              checked={todasMarcadas}
              disabled={selecionaveis.length === 0}
              onChange={evento => onAlternar(selecionaveis, evento.target.checked)}
              className={`h-4 w-4 accent-brand-blue ${FOCO_VISIVEL}`}
            />
            Selecionar todos ({selecionaveis.length})
          </label>
        )}
      </div>

      {total === 0 ? (
        <p className={STATUS}>{filtrando ? 'Nenhuma sugestão com esse nome ou matrícula.' : 'Ninguém para sugerir aqui.'}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {visiveis.map(pessoa => (
            <PessoaDoPainel
              key={pessoa._id}
              pessoa={pessoa}
              naLista={naLista.has(pessoa._id)}
              avaliacao={avaliar(pessoa)}
              marcada={marcadas.has(pessoa._id)}
              onMarcar={marcar => onAlternar([pessoa._id], marcar)}
            />
          ))}
        </ul>
      )}

      {!todasVisiveis && total > VISIVEIS_DE_INICIO && (
        <button
          type="button"
          onClick={() => setAberta(true)}
          className={`self-start text-jj-label font-bold text-brand-blue hover:underline ${FOCO_VISIVEL}`}
        >
          Ver todas ({total})
        </button>
      )}
    </section>
  );
}

interface PessoaDoPainelProps {
  pessoa: PessoaSugerida;
  naLista: boolean;
  avaliacao: AvaliacaoDaInclusao;
  marcada: boolean;
  onMarcar: (marcar: boolean) => void;
}

function PessoaDoPainel({ pessoa, naLista, avaliacao, marcada, onMarcar }: PessoaDoPainelProps) {
  const nome = nomeDoParticipante(pessoa);
  const lotacao = [pessoa.extra?.diretoria, pessoa.extra?.funcao].filter(Boolean).join(' · ');
  const recusa = !naLista && !avaliacao.aceita ? avaliacao.motivo : undefined;

  return (
    <li className="flex items-center gap-2.5 rounded-[10px] border border-surface-300 bg-brand-white px-3 py-2">
      <input
        type="checkbox"
        aria-label={`Selecionar ${nome}`}
        checked={naLista || (marcada && !recusa)}
        disabled={naLista || !!recusa}
        onChange={evento => onMarcar(evento.target.checked)}
        className={`h-4 w-4 shrink-0 accent-brand-blue ${FOCO_VISIVEL}`}
      />
      <RostoDaPessoa pessoa={pessoa} pequeno />
      <div className="min-w-0 flex-1 text-xs text-content">
        <p className="m-0">
          <b>{nome}</b> <span>{pessoa._id}</span>
        </p>
        {lotacao && <p className="m-0 truncate text-jj-micro text-content-soft">{lotacao}</p>}
        {pessoa.origem === 'historico' && (
          <p className="m-0 text-jj-micro text-content-soft">
            {pessoa.vezes ?? 0}× com você
            {pessoa.ultimoPercentual !== undefined && pessoa.ultimoPercentual !== null && ` · Última vez: ${pessoa.ultimoPercentual}%`}
          </p>
        )}
        {recusa && <p className="m-0 text-jj-micro text-brand-blue-dark">{recusa}</p>}
      </div>
      {naLista && <span className="shrink-0 font-display text-[11px] font-bold text-brand-blue-dark">Na lista</span>}
    </li>
  );
}
